import React, { useContext, useEffect } from "react";
import { Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import CustomSelect from "../../CommonComponents/CustomSelect/CustomSelect";
import CustomFormInput from "../../CommonComponents/CustomFormInput/CustomFormInput";
import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import type { DuplicateIdentitySearchBy } from "../../../queries/duplicate-identity/types";
import DuplicateIdentityGroupsTable from "./DuplicateIdentityGroupsTable";
import DuplicateIdentityDetailDrawer from "./DuplicateIdentityDetailDrawer";
import {
  DuplicateIdentityReviewProvider,
  useDuplicateIdentityReview,
  type DuplicateIdentityReviewTab,
} from "./DuplicateIdentityReviewContext";
import styles from "./stylesDuplicateIdentityReview.module.css";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

const SEARCH_BY_OPTIONS: { value: DuplicateIdentitySearchBy; label: string }[] =
  [
    { value: "user_id", label: AppStrings.USER_ID },
    { value: "email", label: AppStrings.EMAIL },
    { value: "phone", label: AppStrings.PHONE_NUMBER },
  ];

function DuplicateIdentityReviewContent() {
  const {
    selectedTab,
    setSelectedTab,
    searchBy,
    setSearchBy,
    searchText,
    setSearchText,
    tenantFilter,
    setTenantFilter,
  } = useDuplicateIdentityReview();

  const tabs: { id: DuplicateIdentityReviewTab; label: string }[] = [
    { id: "PENDING", label: AppStrings.KYC_DUPLICATE_IDENTITY_PENDING_TAB },
    { id: "ACTIONED", label: AppStrings.KYC_DUPLICATE_IDENTITY_ACTIONED_TAB },
  ];

  return (
    <div className={styles.duplicateIdentityReview}>
      <div className={styles.header}>
        <div className={styles.filters}>
          <div className={styles.filterField}>
            <CustomSelect<DuplicateIdentitySearchBy>
              allowClear
              data-testid="duplicate-identity-search-by"
              value={searchBy}
              onChange={(value) => setSearchBy(value)}
              placeholder={AppStrings.NOT_SELECTED}
              options={SEARCH_BY_OPTIONS}
            />
          </div>
          <div className={styles.filterField}>
            <CustomFormInput
              allowClear
              data-testid="duplicate-identity-search-text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={AppStrings.SEARCH}
              prefix={<SearchOutlined />}
            />
          </div>
          <div className={styles.filterField}>
            <Input
              allowClear
              data-testid="duplicate-identity-tenant-filter"
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              placeholder={AppStrings.KYC_DUPLICATE_IDENTITY_TENANT}
            />
          </div>
        </div>
      </div>

      <div
        className={styles.tableTabs}
        role="tablist"
        aria-label={AppStrings.KYC_DUPLICATE_IDENTITY_REVIEW_TITLE}
        data-testid="duplicate-identity-tabs"
      >
        {tabs.map((tab) => {
          const isActive = selectedTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`${styles.tableTab} ${
                isActive ? styles.tableTabActive : ""
              }`}
              data-testid={`duplicate-identity-tab-${tab.id.toLowerCase()}`}
              onClick={() => setSelectedTab(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <DuplicateIdentityGroupsTable />
      <DuplicateIdentityDetailDrawer />
    </div>
  );
}

const DuplicateIdentityReview: React.FC = () => {
  usePageTitle(AppStrings.KYC_DUPLICATE_IDENTITY_REVIEW_TITLE);

  const { setActiveSidebarTab } = useContext(AppContext);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.COMPLIANCE, "DUPLICATE_IDENTITY_REVIEW");
  }, [setActiveSidebarTab]);

  return (
    <DuplicateIdentityReviewProvider>
      <DuplicateIdentityReviewContent />
    </DuplicateIdentityReviewProvider>
  );
};

export default DuplicateIdentityReview;
